import './App.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { PostHogProvider } from './components/posthog-provider';
import { prefetchRouteData } from './prefetch';
import App from './App.tsx';

/**
 * Scripts de terceiros (Firebase reCAPTCHA, session replay de analytics, extensões
 * do navegador) podem mutar o DOM fora do controle do React. Quando o React
 * tenta remover depois um nó que já foi destacado, o `removeChild` nativo lança
 * um `NotFoundError`. Fazer patch nesses dois métodos evita o crash, mantendo
 * um `console.warn` para observabilidade.
 *
 * @see https://github.com/facebook/react/issues/11538
 */
function patchThirdPartyDomConflicts() {
  const proto = Node.prototype as Node & {
    removeChild: Node['removeChild'] & { __menloThirdPartyDomPatch?: true };
    insertBefore: Node['insertBefore'] & { __menloThirdPartyDomPatch?: true };
  };

  // Guard contra double-patching (ex: HMR, execução dupla do módulo).
  if (proto.removeChild.__menloThirdPartyDomPatch && proto.insertBefore.__menloThirdPartyDomPatch) {
    return;
  }

  // Rate limit simples para evitar spam no console em produção.
  const warnCounts = {
    removeChild: 0,
    insertBefore: 0,
  };
  const WARN_LIMIT = 10;
  const warnOnceInAWhile = (kind: keyof typeof warnCounts, ...args: unknown[]) => {
    if (warnCounts[kind] >= WARN_LIMIT) {
      return;
    }
    warnCounts[kind] += 1;
    console.warn(...args);
  };

  const { removeChild, insertBefore } = Node.prototype;

  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      warnOnceInAWhile('removeChild', '[DOM patch] removeChild: nó já estava destacado — nada foi removido', { child });
      return child;
    }
    return removeChild.call(this, child) as T;
  };
  (Node.prototype.removeChild as typeof proto.removeChild).__menloThirdPartyDomPatch = true;

  Node.prototype.insertBefore = function <T extends Node>(newNode: T, refNode: Node | null): T {
    if (refNode && refNode.parentNode !== this) {
      warnOnceInAWhile(
        'insertBefore',
        '[DOM patch] insertBefore: nó de referência estava destacado — newNode NÃO foi inserido',
        { newNode, refNode },
      );
      return newNode;
    }
    return insertBefore.call(this, newNode, refNode) as T;
  };
  (Node.prototype.insertBefore as typeof proto.insertBefore).__menloThirdPartyDomPatch = true;
}

patchThirdPartyDomConflicts();

// Ponto final no hostname (ex: "minha.menlopagamentos.com.br.") é válido para o DNS,
// mas o Firebase Auth não reconhece, causando erro auth/captcha-check-failed.
if (window.location.hostname.endsWith('.')) {
  const url = new URL(window.location.href);
  url.hostname = url.hostname.slice(0, -1);
  window.location.replace(url.toString());
} else {
  // Dispara prefetch de dados e chunks ANTES do React montar.
  // Chunk da página + dados da API carregam em paralelo com o render inicial.
  prefetchRouteData();

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <PostHogProvider>
        <App />
      </PostHogProvider>
    </StrictMode>,
  );

  // Inicializa Datadog após o primeiro paint para não bloquear FCP/LCP
  const scheduleIdle =
    window.requestIdleCallback ??
    ((cb: IdleRequestCallback, opts?: IdleRequestOptions) => window.setTimeout(cb, opts?.timeout ?? 1));
  scheduleIdle(() => import('./services/datadog').then((m) => m.initDatadog()), { timeout: 3000 });
}
